import { ReactNode } from 'react';
import { createGlobalStyle } from 'styled-components';
import {
  Chip,
  ColorsV2,
  Flex,
  Select,
  Text,
  useBreakpoint,
  useThemeColors,
} from '@electricjs/arc';

// Arc's Chip hardcodes its own `:hover`/`:focus`/`:active` background to
// PRIMARY_LIGHTEST whenever it's clickable, which fights with the darker
// background we set for the *selected* pill (visible as a flicker between
// the selected color and the lighter hover/focus color). Chip's own rule is
// class-based (specificity 0,0,2,0); targeting by id (0,1,1,0) reliably wins
// without needing !important, since an id selector always outranks a class
// selector regardless of stylesheet order.
export const SelectedPillInteractionOverride = createGlobalStyle<{
  $chipId: string;
  $color: string;
}>`
  #${props => props.$chipId}:hover,
  #${props => props.$chipId}:focus,
  #${props => props.$chipId}:active {
    background-color: ${props => props.$color};
  }
`;

export type PillFilterOption = {
  value: string;
  label: string;
};

export type PillFilterGroupProps = {
  id: string;
  options: PillFilterOption[];
  selectedValue: string;
  onChange: (value: string) => void;
  ariaLabel?: string;
  /** Extra content (e.g. a divider + an unrelated toggle chip) rendered
   * inline after the pills, sharing the same wrap flow so it doesn't get
   * pushed to its own line once the pills fill the row. */
  trailingContent?: ReactNode;
};

/**
 * Single-select row of pill/chip buttons that wraps on desktop and collapses
 * to a dropdown Select on mobile, where a wrapping/multi-row pill list would
 * eat too much vertical space.
 */
export const PillFilterGroup = ({
  id,
  options,
  selectedValue,
  onChange,
  ariaLabel,
  trailingContent,
}: PillFilterGroupProps) => {
  const { md } = useBreakpoint();
  const [
    selectedBackgroundColor,
    unselectedBackgroundColor,
    selectedInteractionBackgroundColor,
  ] = useThemeColors([
    ColorsV2.PRIMARY,
    ColorsV2.GRAY_LIGHTER,
    ColorsV2.PRIMARY_DARK,
  ]);

  if (!md) {
    const selectedOption =
      options.find(option => option.value === selectedValue) ?? null;
    return (
      <Flex vertical gap="0.8rem" width="100%">
        <Select
          id={id}
          aria-label={ariaLabel}
          options={options}
          value={selectedOption}
          onChange={(option: PillFilterOption | null) =>
            onChange(option?.value ?? options[0].value)
          }
        />
        {trailingContent}
      </Flex>
    );
  }

  return (
    <Flex
      id={id}
      gap="0.8rem"
      flexWrap="wrap"
      role="group"
      aria-label={ariaLabel}>
      <SelectedPillInteractionOverride
        $chipId={`${id}-${selectedValue}`}
        $color={selectedInteractionBackgroundColor}
      />
      {options.map(option => {
        const isSelected = option.value === selectedValue;
        return (
          <Chip
            key={option.value}
            id={`${id}-${option.value}`}
            aria-pressed={isSelected}
            backgroundColor={
              isSelected ? selectedBackgroundColor : unselectedBackgroundColor
            }
            onClick={() => onChange(option.value)}>
            <Text
              variant={isSelected ? 'label-large' : 'body'}
              intent={isSelected ? 'primary-invert' : 'secondary'}>
              {option.label}
            </Text>
          </Chip>
        );
      })}
      {trailingContent}
    </Flex>
  );
};

export default PillFilterGroup;
